import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Card } from 'react-bootstrap';
import { ServerPath } from '../../Constant';
import { toast } from 'react-toastify';
import axios from 'axios';

const SelectLessonModal = ({ show, onHide, onSelect, selectedArtifactId, courseType }) => {
    const [artifacts, setArtifacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(selectedArtifactId || null);

    useEffect(() => {
        if (show) {
            fetchArtifacts();
        }
    }, [show, courseType]);

    // Map course type to artifact types
    const getArtifactTypesByCoursetype = (courseType) => {
        switch (courseType) {
            case 'Video Course':
                return ['LONG_VIDEOS'];
            case 'Audio':
                return ['AUDIO_ORALS'];
            case 'Text-Based Course':
                return ['TEXT_LITERATURE'];
            default:
                return []; // If no course type is specified, don't return any artifact types
        }
    };

    const fetchArtifacts = async () => {
        try {
            setLoading(true);

            // Get artifact types based on course type
            const artifactTypes = getArtifactTypesByCoursetype(courseType);

            if (!artifactTypes.length) {
                console.error('No valid artifact types for course type:', courseType);
                setArtifacts([]);
                setLoading(false);
                return;
            }

            // Fetch artifacts for the specified course type
            const promises = artifactTypes.map(type =>
                axios.get(`${ServerPath}/artifacts`, {
                    params: {
                        type: type,
                        is_a_part_of_course: 1,
                        status: 'ACTIVE'
                    }
                })
            );

            const responses = await Promise.all(promises);

            // Combine results from all responses
            let allArtifacts = [];
            responses.forEach(response => {
                if (response.data.success && response.data.data) {
                    // Additional client-side validation to ensure we only include
                    // artifacts that are ACTIVE and part of a course
                    const validArtifacts = response.data.data.filter(artifact =>
                        artifact.status === 'ACTIVE' &&
                        artifact.is_a_part_of_course === 1
                    );
                    allArtifacts = [...allArtifacts, ...validArtifacts];
                }
            });

            setArtifacts(allArtifacts);
        } catch (error) {
            console.error('Error fetching artifacts:', error);
            setArtifacts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (artifact) => {
        setSelectedItemId(artifact.artifact_id);
        onSelect(artifact);
    };

    const handlePreview = (artifact) => {
        setPreviewUrl(artifact.media_url);
        setShowPreview(true);
    };

    const handleUpload = () => {
        // This would be replaced with your actual upload logic
        toast.info('Upload functionality would be implemented here');
    };

    // Helper to render placeholder if thumbnail is not available
    const renderThumbnail = (artifact) => {
        const placeholderUrl = `https://via.placeholder.com/150x100?text=${encodeURIComponent(artifact.title || 'No Title')}`;

        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{
                    height: '100px',
                    backgroundImage: `url(${artifact.thumbnail_url || placeholderUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                {!artifact.thumbnail_url && (
                    <span className="text-center text-muted small p-2">
                        {artifact.title}
                    </span>
                )}
            </div>
        );
    };

    // Determine appropriate preview component based on artifact type
    const renderPreview = () => {
        if (!previewUrl) return null;

        const isVideo = previewUrl.match(/\.(mp4|webm|ogg)$/i) || previewUrl.includes('video');
        const isAudio = previewUrl.match(/\.(mp3|wav|ogg)$/i) || previewUrl.includes('audio');

        // Check if it's HTML content (either by checking if it starts with HTML tags or contains HTML)
        const isHtmlContent = previewUrl.includes('<') && previewUrl.includes('>');

        if (isVideo) {
            return (
                <video controls style={{ width: '100%', maxHeight: '400px' }}>
                    <source src={previewUrl} />
                    Your browser does not support the video tag.
                </video>
            );
        } else if (isAudio) {
            return (
                <audio controls style={{ width: '100%' }}>
                    <source src={previewUrl} />
                    Your browser does not support the audio tag.
                </audio>
            );
        } else if (isHtmlContent) {
            // For HTML content, render it directly
            return (
                <div
                    style={{
                        width: '100%',
                        height: '400px',
                        overflow: 'auto',
                        padding: '20px',
                        border: '1px solid #ddd',
                        backgroundColor: '#fff'
                    }}
                    dangerouslySetInnerHTML={{ __html: previewUrl }}
                />
            );
        } else {
            // Default to iframe for PDFs and other document types
            return (
                <iframe
                    src={previewUrl}
                    style={{ width: '100%', height: '400px' }}
                    title="Content preview"
                />
            );
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="xl"
            dialogClassName="select-lesson-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Select Lessons
                    {courseType && <span className="text-muted ms-2">({courseType})</span>}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading artifacts...</p>
                    </div>
                ) : artifacts.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="text-muted">No artifacts available for {courseType}. Please upload content first.</p>
                    </div>
                ) : (
                    <Row xs={1} sm={2} md={3} lg={4} className="g-4">
                        {artifacts.map(artifact => (
                            <Col key={artifact.artifact_id}>
                                <Card className="h-90 border" style={{ borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 2 }}>
                                        <input
                                            type="radio"
                                            name="selectArtifact"
                                            id={`select-${artifact.artifact_id}`}
                                            onChange={() => setSelectedItemId(artifact.artifact_id)}
                                            checked={selectedItemId === artifact.artifact_id}
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>

                                    <div style={{
                                        height: '170px',
                                        backgroundImage: `url(${artifact.thumbnail_url || `https://via.placeholder.com/150x100?text=${encodeURIComponent(artifact.title || 'No Title')}`})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}></div>

                                    {/* <div className="p-2">
                                        <p className="mb-0 text-truncate" style={{ fontSize: '14px' }}>
                                            {artifact.title}
                                        </p>
                                    </div> */}

                                    <div className="d-flex" style={{ display: 'flex' }}>
                                        <Button
                                            variant="primary"
                                            className="flex-grow-1 py-2 rounded-0"
                                            onClick={() => handleSelect(artifact)}
                                            disabled={selectedItemId !== artifact.artifact_id}
                                            style={{ backgroundColor: '#6366F1', border: 'none', borderRadius: '0' }}
                                        >
                                            Select
                                        </Button>
                                        <Button
                                            variant="warning"
                                            className="flex-grow-1 py-2 rounded-0"
                                            onClick={() => handlePreview(artifact)}
                                            style={{ backgroundColor: '#F59E0B', border: 'none', borderRadius: '0' }}
                                        >
                                            Preview
                                        </Button>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* <div style={{ position: 'relative', height: '50px' }}>
                    <Button
                        variant="primary"
                        onClick={handleUpload}
                        style={{
                            position: 'absolute',
                            right: '0',
                            backgroundColor: '#6366F1',
                            border: 'none',
                            padding: '8px 24px',
                            borderRadius: '6px'
                        }}
                    >
                        Upload
                    </Button>
                </div> */}
            </Modal.Body>

            {/* Preview Modal */}
            <Modal
                show={showPreview}
                onHide={() => setShowPreview(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Content Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {renderPreview()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPreview(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
};

export default SelectLessonModal;